import { gql } from '@apollo/client';
import { HOME_SPOTS_RECOMMEND, SPOT_GUEST_MEMBERSHIPS } from './spot-fragments';
import { USER_EXTRAS_INFO } from './extra-fragments';

export const USER_CREDITS_INFO = gql`
    fragment UserCredits on User {
        credits {
            available
            inviteTotal
            membershipped
            sniffpassed
            total
        }
    }
`;

export const USER_LOCATION_INFO = gql`
    fragment UserLocationInfo on User {
        latitude
        longitude
        state
        city
        locationSource
    }
`;

export const USER_BASE_INFO = gql`
    fragment UserBaseInfo on User {
        about
        id
        firstname
        lastInitial
        rating
        avatar
        avatarAws
        createdAt
        firstReservationAt
    }
`;

export const USER_SHORT_INFO = gql`
    fragment UserShortInfo on User {
        ...UserBaseInfo
        ...UserLocationInfo
    }
    ${USER_BASE_INFO}
    ${USER_LOCATION_INFO}
`;

export const USER_PAYOUT_INFO = gql`
    fragment UserPayoutInfo on User {
        payoutEnabled
        payoutRequirements
        payoutAccountId
        payoutAccounts {
            id
            bankName
            currency
            last4
        }
        payouts(page: 1, limit: 1) {
            metadata {
                totalCount
                totalEarnings
                monthToDateEarnings
            }
        }
    }
`;

export const USER_LONG_INFO = gql`
    fragment UserLongInfo on User {
        ...UserShortInfo
        ...UserExtras
        ...UserPayoutInfo
        bookingReminders
        phoneNumber
        phoneNumberVerified
        hasUpcomingReservations
        aboutHost
        aboutHostMore
        email
        emailVerified
        lastname
        firstHear
        tosAgree
        vaccinated
        hostRulesAccepted
        rulesAccepted
        totalEarnings
        referralCode
        gender
        birthAt
        race
        newReviewsCount
        pendingReviewsCount
        instagramUsername
        reviewsCount
        reviewsFromGuestsCount
        lastDogsCount
        reservationsCount
        listed
        hostMode
        changeRate
        testGroups
        zipCode
        hostSignupAt
        lastReservationAt
        bookingsLast30Days
        earningsLast30Days
        hostLifetimeVisits
        hostPhotoCount
        dogsVisited
        firstBookingReceivedAt
        hostBookingsCount
        visitsLast28Days
        avgBookingPrice
        creditsExpiringTime
        phoneOptIn
        lastChurnTime
        lastChurnReason
        memberships {
            id
            active
            createdAt
            endsAt
            renewsAt
            canceledAt
            userCanceledAt
            trialEndsAt
            trialStatus
            cancelReason
            cancelReasonPredefined
            remainingCredits
            sniffpassType
            price
            spot {
                id
            }
            status
        }
        hasCanceledSniffpass
        hasCanceledSniffpassTrial
        hasSniffpassTrial
        bannerStatuses
        hostWelcomeMessageBanner
        hostWelcomeMessageText
        hostWelcomeMessagesOn
        hostThankYouMessageText
        hostThankYouMessagesOn
        cards {
            id
            brand
            last4
            expMonth
            expYear
            name
            default
            type
        }
        status
        stripeVerified
        sniffspotSurveys {
            id
            disappointmentLevel
            submittedAt
        }
        tester
    }
    ${USER_EXTRAS_INFO}
    ${USER_SHORT_INFO}
    ${USER_PAYOUT_INFO}
`;

export const USER_HOMEPAGE_INFO = gql`
    fragment UserHomeInfo on User {
        id
        reservationsForReview {
            id
            date
            spot {
                id
                title
                thumbPhoto
            }
        }
        visitAgainSpots {
            ...HomeSpotsRecommend
        }
        otherSpotsYouMayLike {
            ...HomeSpotsRecommend
        }
        recentlyViewedSpots {
            ...HomeSpotsRecommend
        }
    }
    ${HOME_SPOTS_RECOMMEND}
`;

export const SPOT_ALERT_INFO = gql`
    fragment SpotAlertInfo on SpotAlert {
        id
        radius
        enclosureType
        minimumSize
        dogsAllowed
        latitude
        longitude
        active
    }
`;

export const USER_MEMBERSHIP_SUBS = gql`
    fragment UserMembershipSubs on User {
        memberships {
            id
            dogs
            hours
            price
            remainingHours
            remainingCredits
            active
            endsAt
            renewsAt
            status
            sniffpassType
            sniffpassTypeFull
            period
            paused
            pausedAt
            scheduledResumeAt
            resumedAt
            canceledWhilePausedAt
            spot {
                ...SpotGuestMemberships
            }
        }
    }
    ${SPOT_GUEST_MEMBERSHIPS}
`;

export const USER_SPOT_ALERT_FRAGMENT = gql`
    fragment UserSpotAlert on User {
        spotAlert {
            ...SpotAlertInfo
        }
    }
    ${SPOT_ALERT_INFO}
`;
